import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Flame, Mail, Trophy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/Common";
import { api, errMsg, tzOffset } from "@/lib/api";
import { key } from "@/lib/weekPlan";
import { cn } from "@/lib/utils";

export const StreakPills = ({ streak }) => {
  if (!streak) return null;
  const cur = streak.current || 0;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="plan-streak-pills">
      <span className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold", cur ? "border-orange-400/40 bg-orange-500/10 text-orange-300" : "border-[#27272A] text-zinc-400")} data-testid="plan-streak-pill">
        <Flame className="h-3.5 w-3.5" fill={cur ? "currentColor" : "none"} />{cur ? `Plan streak · ${cur} day${cur === 1 ? "" : "s"}` : `Plan streak · best ${streak.best || 0} days`}
      </span>
      <span className="flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#33290D] px-3 py-1 text-xs font-semibold text-[#F3C649]" data-testid="plan-perfect-pill">
        <Trophy className="h-3.5 w-3.5" />{streak.perfect_weeks || 0} perfect week{streak.perfect_weeks === 1 ? "" : "s"}
      </span>
    </div>
  );
};

export const PlanProgress = ({ progress }) => {
  const pct = progress?.pct || 0;
  return (
    <div data-testid="plan-progress">
      <div className="mb-1.5 flex justify-between text-xs text-zinc-400">
        <span data-testid="plan-progress-text">{progress?.done_count || 0} / {progress?.total || 0} tasks · {pct}%</span>
        {progress?.streak?.week_complete && <span className="font-semibold text-[#F3C649]">Week complete</span>}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3C649] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export const PlanTask = ({ text, done, onToggle, testid }) => (
  <button onClick={onToggle} data-testid={testid} aria-pressed={done} className="group flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[#18181B]">
    <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors", done ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-zinc-600 group-hover:border-[#D4AF37]/70")}>{done && <Check className="h-3.5 w-3.5" strokeWidth={3} />}</span>
    <span className={cn("text-sm leading-snug transition-colors", done ? "text-zinc-500 line-through" : "text-zinc-200")}>{text}</span>
  </button>
);

export const PlanDay = ({ section, si, done, onToggle, today, className }) => {
  const items = section.items || [];
  const n = items.filter((_, i) => done.has(key(si, i))).length;
  return (
    <div className={cn("rounded-2xl border bg-[#121214] p-4", today ? "border-[#D4AF37]/60 shadow-[0_0_30px_-12px_rgba(212,175,55,0.5)]" : "border-[#27272A]", className)} data-testid={`plan-day-${si}`}>
      <div className="mb-2 flex items-center justify-between px-2">
        <h4 className="flex items-center gap-2 font-display text-base text-zinc-100">{section.heading}{today && <span className="rounded-full bg-[#D4AF37] px-2 py-px text-[10px] font-bold uppercase tracking-wider text-black">Today</span>}</h4>
        <span className={cn("font-mono text-xs", items.length && n === items.length ? "text-[#F3C649]" : "text-zinc-500")} data-testid={`plan-day-count-${si}`}>{n}/{items.length}</span>
      </div>
      {items.length ? items.map((t, i) => <PlanTask key={i} text={t} done={done.has(key(si, i))} onToggle={() => onToggle(si, i, !done.has(key(si, i)))} testid={`plan-task-${si}-${i}`} />) : <p className="px-2 py-1 text-xs text-zinc-500">Rest day. Nothing planned.</p>}
    </div>
  );
};

export const MorningEmailCard = () => {
  const [d, setD] = useState(undefined);
  useEffect(() => { api.get("/calendar/plan-email").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  const toggle = async (enabled) => {
    const prev = d; setD({ ...d, enabled });
    try { setD((await api.put("/calendar/plan-email", { enabled, tz_offset_minutes: tzOffset() })).data); toast.success(enabled ? "Morning plan email on" : "Morning plan email off"); }
    catch (e) { setD(prev); toast.error(errMsg(e)); }
  };
  const send = async () => { try { await api.post("/calendar/plan-email/send-now"); toast.success("Today's plan is on its way to your inbox"); } catch (e) { toast.error(errMsg(e)); } };
  if (d === null) return null;
  return (
    <Card data-testid="plan-email-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Morning plan email</h3></div>
        {d === undefined ? <Spinner className="text-[#D4AF37]" /> : <Switch checked={!!d.enabled} onCheckedChange={toggle} data-testid="plan-email-switch" />}
      </div>
      <p className="mt-2 text-sm text-zinc-500">Today's tasks, your weekly progress and streak, in your inbox at {d?.hour ?? 8}:00 your time.</p>
      <Button variant="outline" onClick={send} className="mt-4 w-full" data-testid="plan-email-send-now-button">Send me today's plan</Button>
    </Card>
  );
};
